import { useMemo } from "react";
import type { NavItem } from "@/config/navConfig";
import { useUserStore } from "@/store/useUserStore";
import { useFilteredNav } from "./useFilteredNav";

export interface NavGroup {
  key: "pinned" | "platform" | "workspace";
  label: string | null;
  items: NavItem[];
}

/**
 * True when the user works across multiple workspaces and therefore gets the
 * top-bar workspace switcher — super admins always, partners only when they
 * manage more than one workspace. Kept in one place so the Topbar switcher and
 * the grouped navigation can never disagree about when to show.
 */
export const useShowWorkspaceSwitcher = (): boolean => {
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin);
  const isPartner = useUserStore((s) => s.isPartner);
  const workspaceCount = useUserStore((s) => s.workspaces.length);
  return isSuperAdmin || (isPartner && workspaceCount > 1);
};

/**
 * Pure grouping of an already role/permission-filtered nav list into scope
 * sections. Order: pinned (Overview) → platform → workspace; empty groups are
 * dropped so a section header never renders without items. Exported for testing.
 */
export const buildNavGroups = (
  filtered: NavItem[],
  opts: { showSections: boolean; isSuperAdmin: boolean },
): { showSections: boolean; groups: NavGroup[] } => {
  if (!opts.showSections) {
    return { showSections: false, groups: [{ key: "pinned", label: null, items: filtered }] };
  }

  // Super admins administer the whole platform; partners only span their own
  // workspaces, so give them a label that doesn't imply tenant-wide control.
  const platformLabel = opts.isSuperAdmin ? "Platform" : "Across My Workspaces";

  const groups: NavGroup[] = [
    { key: "pinned", label: null, items: filtered.filter((i) => !i.section) },
    { key: "platform", label: platformLabel, items: filtered.filter((i) => i.section === "platform") },
    { key: "workspace", label: "Current Workspace", items: filtered.filter((i) => i.section === "workspace") },
  ];
  return { showSections: true, groups: groups.filter((g) => g.items.length > 0) };
};

/**
 * Role/permission-filtered navigation, grouped into scope sections for users who
 * work across workspaces (super admins / multi-workspace partners) and left flat
 * for everyone else.
 */
export const useGroupedNav = (): { showSections: boolean; groups: NavGroup[] } => {
  const filtered = useFilteredNav();
  const showSections = useShowWorkspaceSwitcher();
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin);

  return useMemo(
    () => buildNavGroups(filtered, { showSections, isSuperAdmin }),
    [filtered, showSections, isSuperAdmin],
  );
};
